import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';

const ToolCard = React.memo(({ tool }) => {
  const [isLoading, setIsLoading] = useState({});

  const getPriceClass = (priceType) => {
    switch (priceType) {
      case 'free': return 'price-free';
      case 'lifetime': return 'price-lifetime';
      case 'custom': return 'price-custom';
      default: return 'price-custom';
    }
  };

  const handleExternalLink = async (url, actionId) => {
    if (url.startsWith('http')) {
      setIsLoading(prev => ({ ...prev, [actionId]: true }));
      
      // Simulate loading delay for better UX
      setTimeout(() => {
        window.open(url, '_blank', 'noopener,noreferrer');
        setIsLoading(prev => ({ ...prev, [actionId]: false }));
      }, 800);
    }
  };

  return (
    <motion.div 
      className="tool-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6 }}
      whileHover={{ 
        y: -8,
        transition: { duration: 0.3, type: "spring", stiffness: 200 }
      }}
    >
      <div className="tool-header">
        <motion.div 
          className="tool-icon"
          whileHover={{ 
            scale: 1.1, 
            rotate: 5,
            transition: { duration: 0.3 }
          }}
        >
          {tool.icon}
        </motion.div>
        <div>
          <div className="tool-title">{tool.title}</div>
          <motion.div 
            className={`tool-price ${getPriceClass(tool.priceType)}`}
            whileHover={{ scale: 1.05 }}
          >
            {tool.price}
          </motion.div>
        </div>
      </div>
      
      <div className="tool-description">
        <ReactMarkdown>{tool.description}</ReactMarkdown>
      </div>
      
      <div className="tool-tags">
        {tool.tags.map((tag, index) => (
          <motion.span 
            key={index} 
            className="tag"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1, duration: 0.3 }}
            whileHover={{ 
              scale: 1.05,
              backgroundColor: 'rgba(0, 255, 136, 0.1)',
              borderColor: 'rgba(0, 255, 136, 0.3)',
              color: '#00ff88'
            }}
          >
            {tag}
          </motion.span>
        ))}
      </div>
      
      <div className="tool-actions">
        {tool.actions.map((action, index) => (
          <motion.a
            key={index}
            href={action.url.startsWith('#') ? action.url : '#'}
            className={`btn ${action.type} ${isLoading[`${tool.id}-${index}`] ? 'loading' : ''}`}
            onClick={(e) => {
              if (action.url.startsWith('http')) {
                e.preventDefault();
                handleExternalLink(action.url, `${tool.id}-${index}`);
              }
            }}
            whileHover={{ 
              scale: 1.05,
              y: -2,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.95 }}
            target={action.url.startsWith('http') ? '_blank' : '_self'}
            rel={action.url.startsWith('http') ? 'noopener noreferrer' : ''}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
          >
            {isLoading[`${tool.id}-${index}`] ? (
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                ⚡
              </motion.span>
            ) : (
              action.label
            )}
          </motion.a>
        ))}
      </div>
    </motion.div>
  );
});

ToolCard.displayName = 'ToolCard';

export default ToolCard;